"use client";

import { useEffect, useRef } from "react";

/**
 * "Which roles does this member actually look at?"
 *
 * The signal the board was not recording. Saved jobs and alerts are too rare to
 * bucket anyone on, so browsing is the only honest evidence of what someone is
 * looking for — and it is what the lifecycle mail reads to decide whether it
 * can say something true about their corner of the sector instead of "new jobs
 * are live".
 *
 * Deliberately small and deliberately late:
 *  - it fires once per mount, after a delay, so it never competes with the page
 *    the reader came for;
 *  - the route ignores anyone not signed in, so crawlers and unfurlers write
 *    nothing;
 *  - it stores the job's category, never anything finer about the person;
 *  - it always resolves. A signal is not worth an error in front of a reader.
 */
export function JobViewSignal({ jobId }: { jobId: number }) {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !Number.isFinite(jobId)) return;
    fired.current = true;
    const t = window.setTimeout(() => {
      void fetch("/api/signals/job-view", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobId }),
        keepalive: true,
      }).catch(() => {});
    }, 1500);
    return () => window.clearTimeout(t);
  }, [jobId]);

  return null;
}
